import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Card,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Tooltip,
  Autocomplete,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Typography,
  Snackbar,
  Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackTwoToneIcon from '@mui/icons-material/ArrowBackTwoTone';
import { useTranslation } from 'react-i18next';
import { Can } from '@casl/react';
import { userService } from '@/data/userService';
import { userBranchService } from '@/data/userBranchService';
import { branchService } from '@/data/branchService';
import { Branch } from '@/types/Branch.interface';
import { AssignedBranch } from '@/types/UserBranch.interface';
import { Role } from '@/enums/role';
import AbilityContext from '@/contexts/AbilityContext';
import PageTitleWrapper from '@/components/PageTitleWrapper';
import StatusLabel from '@/components/StatusLabel';

interface AlertState {
  open: boolean;
  message: string;
  severity: 'error' | 'warning' | 'info' | 'success';
}

// HTTP statuses the global axios interceptor (apiService.ts) already turns into
// a toast. For any other status (e.g. 400/422 validation) or a response-less
// network error, the page surfaces its own message so failures are never silent.
const INTERCEPTOR_HANDLED_STATUSES = new Set([
  401, 403, 404, 409, 413, 415, 500
]);

const UserBranchManagement = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const ability = useContext(AbilityContext);

  const [userBranches, setUserBranches] = useState<AssignedBranch[]>([]);
  const [availableBranches, setAvailableBranches] = useState<Branch[]>([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [branchToRemove, setBranchToRemove] = useState<AssignedBranch | null>(
    null
  );
  const [userName, setUserName] = useState('');
  const [userRole, setUserRole] = useState('');
  const [userCompanyId, setUserCompanyId] = useState<number | null>(null);
  const [alert, setAlert] = useState<AlertState>({
    open: false,
    message: '',
    severity: 'info'
  });

  const showAlert = (message: string, severity: AlertState['severity']) => {
    setAlert({ open: true, message, severity });
  };

  // Avoid double toasts: defer to the global interceptor for the statuses it
  // handles, but surface everything else (unhandled statuses, network errors)
  // so a failure is never silently swallowed.
  const notifyError = (error: any, fallbackKey: string) => {
    const status = error?.response?.status;
    if (status === undefined || !INTERCEPTOR_HANDLED_STATUSES.has(status)) {
      showAlert(t(fallbackKey), 'error');
    }
  };

  const fetchUserBranches = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const userData = await userService.getById(Number(id));
      setUserName(`${userData.name} ${userData.surname}`);
      setUserRole(userData.role);
      setUserCompanyId(userData.company?.id ?? null);

      const branchData = await userBranchService.getUserBranches(Number(id));
      setUserBranches(branchData);
    } catch (error) {
      console.error('Error fetching user branches:', error);
      notifyError(error, 'user.branch.fetch.error');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchAvailableBranches = async () => {
    setBranchesLoading(true);
    try {
      const allBranches = await branchService.getAllFlat({
        getAll: true,
        ...(userCompanyId ? { companyId: userCompanyId } : {})
      });

      const assignedIds = new Set(userBranches.map((ub) => ub.id));
      const filteredBranches = allBranches.filter(
        (branch: Branch) =>
          !assignedIds.has(branch.id) &&
          (!userCompanyId || branch.companyId === userCompanyId)
      );

      setAvailableBranches(filteredBranches);
    } catch (error) {
      console.error('Error fetching available branches:', error);
      notifyError(error, 'branch.available.fetch.error');
    } finally {
      setBranchesLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchUserBranches();
    }
    // fetchUserBranches is stable for a given route id; refetch only on id change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (addDialogOpen) {
      fetchAvailableBranches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addDialogOpen, userBranches, userCompanyId]);

  const handleSetPrimary = async (branchId: number) => {
    if (actionLoading) return;

    setActionLoading(true);
    try {
      await userBranchService.setPrimaryBranch(Number(id), branchId);
      await fetchUserBranches(true);
      showAlert(t('branch.primary.set.success'), 'success');
    } catch (error) {
      console.error('Error setting primary branch:', error);
      notifyError(error, 'branch.primary.set.error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveBranch = async () => {
    if (!branchToRemove) return;

    // Client-side guard: a branch admin must keep at least one branch.
    if (userRole === Role.BRANCH_ADMIN && userBranches.length === 1) {
      showAlert(t('branch.remove.only.error'), 'error');
      setBranchToRemove(null);
      return;
    }

    setActionLoading(true);
    try {
      await userBranchService.removeBranch(Number(id), branchToRemove.id);
      await fetchUserBranches(true);
      showAlert(t('branch.removed.success'), 'success');
    } catch (error) {
      console.error('Error removing branch:', error);
      notifyError(error, 'branch.remove.error');
    } finally {
      setActionLoading(false);
      setBranchToRemove(null);
    }
  };

  const handleAddBranch = async () => {
    if (!selectedBranch || actionLoading) return;

    // The first assigned branch is created as the primary one in a single call.
    const makePrimary = userBranches.length === 0;
    setActionLoading(true);
    try {
      await userBranchService.addBranch(
        Number(id),
        selectedBranch.id,
        makePrimary
      );
      setAddDialogOpen(false);
      setSelectedBranch(null);
      showAlert(t('branch.added.success'), 'success');
    } catch (error) {
      console.error('Error adding branch:', error);
      notifyError(error, 'branch.add.error');
    } finally {
      setActionLoading(false);
      fetchUserBranches(true);
    }
  };

  const handleCloseAddDialog = () => {
    if (actionLoading) return;
    setAddDialogOpen(false);
    setSelectedBranch(null);
  };

  const handleBackToUserDetails = () => {
    navigate(`/management/users/details/${id}`);
  };

  const handleCloseAlert = () => {
    setAlert((prev) => ({ ...prev, open: false }));
  };

  const branchAdminHasSingleBranch =
    userRole === Role.BRANCH_ADMIN && userBranches.length === 1;

  // The primary branch can only be removed once it is the last one left,
  // so users promote another branch first.
  const isRemoveDisabled = (branch: AssignedBranch) =>
    actionLoading ||
    (branch.isPrimary && userBranches.length > 1) ||
    branchAdminHasSingleBranch;

  return (
    <>
      <Helmet>
        <title>{t('user.branch.management')}</title>
      </Helmet>

      <PageTitleWrapper>
        <Box display="flex" alignItems="center">
          <Tooltip arrow placement="top" title={t('back.to.users')}>
            <IconButton
              onClick={handleBackToUserDetails}
              color="primary"
              sx={{ p: 1, mr: 2 }}
            >
              <ArrowBackTwoToneIcon />
            </IconButton>
          </Tooltip>
          <Box>
            <Typography variant="h3" component="h3" gutterBottom>
              {t('branch.management')} - {userName}
            </Typography>
            <Typography variant="subtitle2">
              {t('user.branch.management')}
            </Typography>
          </Box>
        </Box>
      </PageTitleWrapper>

      <Container maxWidth="lg">
        <Card>
          <CardHeader
            title={t('assigned.branches')}
            action={
              <Can I="update" a="User" ability={ability}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setAddDialogOpen(true)}
                >
                  {t('add.branch')}
                </Button>
              </Can>
            }
          />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('branch.name')}</TableCell>
                  <TableCell>{t('mapcode')}</TableCell>
                  <TableCell>{t('branch.status')}</TableCell>
                  <TableCell align="center">{t('primary')}</TableCell>
                  <TableCell align="right">{t('actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : userBranches.length > 0 ? (
                  userBranches.map((branch) => (
                    <TableRow key={branch.id}>
                      <TableCell>{branch.name}</TableCell>
                      <TableCell>{branch.mapcode || '—'}</TableCell>
                      <TableCell>
                        <StatusLabel status={branch.status} />
                      </TableCell>
                      <TableCell align="center">
                        <Can I="update" a="User" ability={ability}>
                          <Tooltip
                            title={
                              branch.isPrimary
                                ? t('primary.branch')
                                : t('set.as.primary')
                            }
                          >
                            <span>
                              <IconButton
                                onClick={() =>
                                  !branch.isPrimary &&
                                  handleSetPrimary(branch.id)
                                }
                                color={branch.isPrimary ? 'primary' : 'default'}
                                disabled={branch.isPrimary || actionLoading}
                              >
                                {branch.isPrimary ? (
                                  <StarIcon />
                                ) : (
                                  <StarOutlineIcon />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Can>
                      </TableCell>
                      <TableCell align="right">
                        <Can I="update" a="User" ability={ability}>
                          <Tooltip title={t('remove.branch')}>
                            <span>
                              <IconButton
                                onClick={() => setBranchToRemove(branch)}
                                color="error"
                                disabled={isRemoveDisabled(branch)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Can>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography>{t('no.branches.assigned')}</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Add Branch Dialog */}
        <Dialog
          open={addDialogOpen}
          onClose={handleCloseAddDialog}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>{t('add.branch')}</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Autocomplete
                options={availableBranches}
                loading={branchesLoading}
                getOptionLabel={(option) => option.name ?? ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField {...params} label={t('select.branch')} />
                )}
                value={selectedBranch}
                onChange={(_, newValue) => setSelectedBranch(newValue)}
                fullWidth
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddDialog} disabled={actionLoading}>
              {t('cancel')}
            </Button>
            <Button
              onClick={handleAddBranch}
              variant="contained"
              disabled={!selectedBranch || actionLoading}
              startIcon={
                actionLoading ? <CircularProgress size={16} /> : undefined
              }
            >
              {t('add')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Remove Branch Confirmation Dialog */}
        <Dialog
          open={Boolean(branchToRemove)}
          onClose={() => !actionLoading && setBranchToRemove(null)}
        >
          <DialogTitle>{t('remove.branch.confirm.title')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('remove.branch.confirm.message', {
                branch: branchToRemove?.name ?? ''
              })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setBranchToRemove(null)}
              disabled={actionLoading}
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={handleRemoveBranch}
              variant="contained"
              color="error"
              disabled={actionLoading}
              startIcon={
                actionLoading ? <CircularProgress size={16} /> : undefined
              }
            >
              {t('remove')}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={alert.open}
          autoHideDuration={6000}
          onClose={handleCloseAlert}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseAlert}
            severity={alert.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {alert.message}
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
};

export default UserBranchManagement;
